import { AiTwotoneEdit } from "react-icons/ai";
import { IoIosSend } from "react-icons/io";

import moment from "moment/moment";

// Utils
import { getRemainingDate } from "../../utils/utils";
import { Avatar } from "@chakra-ui/react";
import { useSelector } from "react-redux";

/**
 * CommentCard component with reply functionality
 */
function CommentCard({
  data,                    // Comment data object
  index,                   // Unique index for the comment
  userData,                // Current user data
  showNestedComment,       // Boolean to control nested comment visibility
  nestedComment,           // Array of nested comments
  onReply,                 // Boolean to control reply form visibility
  replyId,                 // ID of the comment being replied to
  getAllNestedComments,    // Function to fetch nested comments
  showReplyBox,            // Function to toggle reply box
  handleReplyComment,      // Function to submit a reply
  setReplyMessage,         // Function to update reply message
  replyMessage,            // Current reply message text
  handleOpenEditComment,   // Function to open edit comment mode
}) {
    const darktheme = useSelector((state) => state.features.settings.darktheme);
  /**
   * Renders a nested comment
   * @param {Object} item - The nested comment data
   * @returns {JSX.Element} - Nested comment component
   */
  const renderNestedComment = (item) => {
    if (item.commentId !== data?.commentId) return null;

    return (
      <div className="flex">
        <div className="mr-2 w-[40px] h-[40px] rounded-[50%]">

          <Avatar
            name={item?.username}
            src={item?.userImage}
            width={"20px"}
            height={"20px"}
          />
        </div>
        <div className="">
          <div className="flex items-center">
            <div className={`mr-[10px] ${darktheme ? "text-white" : "text-[#667085]"}`}>{item?.username}</div>
            <div className={`text-[12px] ${darktheme ? "text-white" : "text-[#667085]"}`}>
              {getRemainingDate(item?.createdAt)}
            </div>
          </div>
          {/* Comment text */}
          <div className={`text-[13px] ${darktheme ? "text-white" : "text-[#667085]"}`}>
            {item?.text}
          </div>
          <div className="flex items-center">
            <p
              className={`cursor-pointer text-[.7rem] hover:bg-[#f2f2f2] ${darktheme ? "text-white" : "text-[#667085]"} p-[px]`}
              onClick={() => showReplyBox(!onReply, item?.commentId)}
            >
              Reply
            </p>
            <div className="mx-2">.</div>
          </div>
          <button
            className="text-[.7rem]"
            onClick={() => getAllNestedComments(item.commentId)}
          >
            --View More replies
          </button>
        </div>
      </div>
    );
  };

  /**
   * Renders the reply input box
   * @returns {JSX.Element} - Reply form component
   */
  const renderReplyBox = () => {
    if (!onReply || replyId !== data.commentId) return null;

    return (
      <div className={`w-full h-full flex ${darktheme ? "bg-black" : "bg-[#F7F7F7]"} mt-1 rounded-lg pl-1`}>
        <div className="w-full h-[32px]">
          <input
            className="w-full h-full bg-transparent border-none outline-none px-2 text-xs"
            value={replyMessage}
            placeholder="reply...."
            onChange={(e) => setReplyMessage(e.target.value)}
          />
        </div>
        <IoIosSend
          className="hover:cursor-pointer hover:bg-slate-500 rounded-sm flex items-center px-1"
          size={30}
          onClick={() =>
            handleReplyComment(
              data.commentId,
              data.username,
              data.displayName
            )
          }
        />
      </div>
    );
  };

  return (
    <div className={`w-full flex`} key={index}>
      <div className="flex">
        {/* User avatar */}
        <div className="">
          <div className="mr-2">
            <Avatar
              name={data?.username}
              src={data?.userImage}
              width={"25px"}
              height={"25px"}
            />
          </div>
        </div>

        <div className="">
          {/* User info and timestamp */}
          <div className="flex gap-2 items-center">
            <h4 className={`${darktheme ? "text-white" : "text-[#1C2024]"}  text-xs font-semibold capitalize`}>
              {data?.username}
            </h4>
            <div className={`text-[10px] ${darktheme ? "text-white" : "text-[#718096]"}`}>
              {moment(data.createdAt).format(" Do MMM YY, h:mm a")}
            </div>
          </div>

          {/* Main comment text */}
          <div className={`text-xs ${darktheme ? "text-white" : "text-[#425466]"}`}>{data.text}</div>

          {/* Reply button */}
          <div className="flex items-center">
            <p
              className={`cursor-pointer hover:bg-[#f2f2f2] hover:px-2 hover:rounded-xl text-[10px] ${darktheme ? "text-[#425420]" : "text-[#425466]"} p-0.5`}
              onClick={() => showReplyBox(!onReply, data.commentId)}
            >
              {!onReply ? "reply" : "hide reply box"}
            </p>
          </div>

          {/* Nested comments */}
          {showNestedComment && nestedComment?.map(renderNestedComment)}

          {/* Reply input box */}
          {renderReplyBox()}

        </div>
      </div>

      {/* Edit icon - only shown for comment owner */}
      {userData.user_id === data.userId && (
        <div className="ml-2 cursor-pointer" onClick={() => handleOpenEditComment(data)}>
          <AiTwotoneEdit />
        </div>
      )}
    </div>
  );
}

export default CommentCard;